import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { getSortFieldForApi } from 'src/helpers/sort';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { useIntegrationService, useIntegrationsService } from '../services';
import { Integration, IntegrationFormData } from '../types';
import { useAddIntegrationModalController } from './AddIntegrationModalController';

const ITEMS_PER_PAGE = 25;
// Paused hooks stay listed: the API returns only active ones unless asked
const LISTED_STATUSES = 'active,paused';
const HOOK_ID_PARAM = 'hookId';

export const useIntegrationsViewController = (profileRole: string) => {
  const selectedCompanyId = useSelector(getCurrentCompanyId);
  const [page, setPage] = useState(0);
  const [sortField, setSortField] = useState('name_asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [integrations, setIntegrations] = useState<{ count: number; items: Integration[] | null }>({
    count: 0,
    items: null,
  });
  const [isLoading, setIsLoading] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const deliveryLogHookId = searchParams.get(HOOK_ID_PARAM);

  const { getIntegrations } = useIntegrationsService();
  const { pauseIntegration, activateIntegration, deleteIntegration } = useIntegrationService();
  const addIntegrationModalController = useAddIntegrationModalController();
  const {
    open: openModal,
    close: closeModalBase,
    submit: submitModal,
  } = addIntegrationModalController;

  const isButtonsDisabled = !['bhi_admin', 'company_admin'].includes(profileRole);

  const refreshIntegrations = useCallback(async () => {
    if (!selectedCompanyId) {
      return;
    }

    setIsLoading(true);
    try {
      const data = await getIntegrations({
        companyId: selectedCompanyId,
        limit: ITEMS_PER_PAGE,
        offset: page * ITEMS_PER_PAGE,
        sort: getSortFieldForApi(sortField),
        query: searchQuery || undefined,
        status: LISTED_STATUSES,
      });
      setIntegrations(data);
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCompanyId, page, sortField, searchQuery, getIntegrations]);

  const openCreateModal = useCallback(() => {
    openModal();
  }, [openModal]);

  const openEditModal = useCallback(
    (integrationId: string) => {
      const integration = integrations.items?.find(({ id }) => id === integrationId);
      openModal(integration);
    },
    [integrations.items, openModal],
  );

  const closeModal = useCallback(() => {
    closeModalBase();
    refreshIntegrations();
  }, [closeModalBase, refreshIntegrations]);

  const submit = useCallback(
    async (formData: IntegrationFormData) => {
      const result = await submitModal(formData);
      if (!result) {
        await refreshIntegrations();
      }
      return result;
    },
    [submitModal, refreshIntegrations],
  );

  const removeIntegration = useCallback(
    async (integrationId: string) => {
      if (!selectedCompanyId) {
        return;
      }
      await deleteIntegration(integrationId, selectedCompanyId);
      await refreshIntegrations();
    },
    [selectedCompanyId, deleteIntegration, refreshIntegrations],
  );

  const changeStatus = useCallback(
    async (change: (integrationId: string, companyId: string) => Promise<unknown>, integrationId: string, verb: string) => {
      if (!selectedCompanyId) {
        return;
      }
      try {
        await change(integrationId, selectedCompanyId);
        toast.success(`Webhook ${verb}d`);
      } catch (e) {
        console.error(e);
        toast.error(`Failed to ${verb} the webhook. Please try again.`);
      }
      await refreshIntegrations();
    },
    [selectedCompanyId, refreshIntegrations],
  );

  const pause = useCallback(
    (integrationId: string) => changeStatus(pauseIntegration, integrationId, 'pause'),
    [changeStatus, pauseIntegration],
  );

  const activate = useCallback(
    (integrationId: string) => changeStatus(activateIntegration, integrationId, 'activate'),
    [changeStatus, activateIntegration],
  );

  // The log is addressed by the URL, so the link in the "webhook paused" email opens it directly
  const openDeliveryLog = useCallback(
    (integrationId: string) => {
      setSearchParams(params => {
        const next = new URLSearchParams(params);
        next.set(HOOK_ID_PARAM, integrationId);
        return next;
      });
    },
    [setSearchParams],
  );

  const closeDeliveryLog = useCallback(() => {
    setSearchParams(params => {
      const next = new URLSearchParams(params);
      next.delete(HOOK_ID_PARAM);
      return next;
    });
  }, [setSearchParams]);

  const changeSearch = useCallback((_: ChangeEvent<HTMLInputElement>, query: string) => {
    setSearchQuery(query);
  }, []);

  const changeSorting = useCallback((s: GridSortModel) => {
    if (s[0]) {
      setSortField(`${s[0].field}_${s[0].sort}`);
    }
  }, []);

  const changePagination = useCallback((p: GridPaginationModel) => {
    setPage(p.page);
  }, []);

  useEffect(() => {
    refreshIntegrations();
  }, [refreshIntegrations]);

  useEffect(() => {
    setPage(0);
  }, [sortField, searchQuery]);

  return {
    isLoading,
    isButtonsDisabled,
    profileRole,
    searchQuery,
    sortField,
    page,
    itemsPerPage: ITEMS_PER_PAGE,
    integrations,
    changeSearch,
    changeSorting,
    changePagination,
    openCreateModal,
    openEditModal,
    removeIntegration,
    pause,
    activate,
    selectedCompanyId,
    deliveryLogHookId,
    openDeliveryLog,
    closeDeliveryLog,
    refreshIntegrations,
    addIntegrationModalController: {
      ...addIntegrationModalController,
      close: closeModal,
      submit,
    },
  };
};
